import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sun, Moon, Sparkles, Menu, X, ArrowLeft, Instagram, MoreHorizontal } from "lucide-react";
import LanguageSelector from "./LanguageSelector";
import SoundToggle from "./SoundToggle";
import SoundCue from "./SoundCue";
import { ExternalLink } from "./ExternalLink";
import PolicyModal from "./PolicyModal";
import { usePreferences } from "../context/PreferencesContext";

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { openPrivacy } = usePreferences();
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("thriving-theme") || "dark";
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Close "More" menu when route changes
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
      root.style.backgroundColor = "#121212";
    } else {
      root.classList.remove("dark");
      root.style.backgroundColor = "#FDFBF7";
    }
    localStorage.setItem("thriving-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => (prev === "light" ? "dark" : "light"));
  };

  const navItems = [
    { name: t("nav.home"), path: "/", id: "home" },
    { name: t("nav.about"), path: "/about", id: "about" },
    { name: t("nav.world"), path: "/world", id: "world" },
    { name: t("nav.journal"), path: "/journal", id: "journal" },
    { name: t("nav.community"), path: "/community", id: "community" },
    { name: t("nav.resources"), path: "/resources", id: "resources" },
    { name: t("nav.startHere"), path: "/start-here", id: "start-here" },
    { name: t("nav.contact"), path: "/contact", id: "contact" }
  ];

  // Primary desktop nav — visible inline on lg and above
  const primaryNav = navItems.filter((i) => ["about", "world", "journal", "community", "resources"].includes(i.id));
  // Secondary desktop nav — behind the "More" overflow menu
  const secondaryNav = navItems.filter((i) => ["start-here", "contact"].includes(i.id));

  return (
    <div className="min-h-screen bg-primary-custom text-primary-custom flex flex-col selection:bg-[var(--accent-bronze)] selection:text-white">
      {/* Global ambient sound control — persists across every page */}
      <SoundToggle />
      {/* Discreet first-visit invitation into the soundscape */}
      <SoundCue />
      {/* Editorial Header — light mode uses an opaque warm-ivory bar so the
          hero video behind doesn't tint the header grey; dark mode unchanged. */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7] dark:bg-[#121212]/80 border-b border-subtle-custom transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 lg:px-8 h-20 flex items-center justify-between gap-6">
          
          {/* Logo / Brand Name */}
          <Link 
            to="/" 
            className="flex items-center space-x-2 sm:space-x-3 group min-w-0 shrink"
            data-testid="header-logo-link"
          >
            <div className="w-8 h-8 rounded-none border border-subtle-custom flex items-center justify-center bg-surface-custom group-hover:border-[var(--accent-bronze)] transition-all duration-300 shrink-0">
              <span className="font-serif text-sm font-semibold tracking-widest text-primary-custom">T</span>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-serif tracking-[0.15em] text-sm font-semibold uppercase text-primary-custom whitespace-nowrap">
                The Thriving Life
              </span>
              {/* Tagline is hidden on very narrow screens (< sm = 640 px) so the
                  header actions fit without horizontal overflow on iPhone-class
                  widths. Reappears at sm and above. */}
              <span className="hidden sm:block text-[9px] font-sans tracking-[0.3em] uppercase text-secondary-custom whitespace-nowrap">
                {t("brand.tagline")}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation — inline from lg (1024px). Lighter typography,
              balanced spacing, no borders. Start Here / Contact live behind a
              discreet "More" overflow so the primary row stays quiet. */}
          <nav className="hidden lg:flex items-center gap-7 xl:gap-9 flex-1 justify-center">
            {primaryNav.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.id}`}
                  className={`relative text-[11px] font-sans uppercase tracking-[0.18em] whitespace-nowrap transition-colors duration-300 ${
                    isActive
                      ? "text-primary-custom"
                      : "text-secondary-custom hover:text-primary-custom"
                  }`}
                >
                  {item.name}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-px bg-[var(--accent-bronze)]" aria-hidden="true" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Action / Toggle Zone — quieter, borderless icon buttons */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Desktop "More" overflow: Start Here + Contact */}
            <div className="relative hidden lg:block" ref={moreRef}>
              <button
                onClick={() => setMoreOpen((o) => !o)}
                data-testid="nav-more-toggle"
                aria-label="More links"
                aria-expanded={moreOpen}
                className={`p-2 -mx-1 rounded-none transition-colors duration-300 ${
                  moreOpen ? "text-primary-custom" : "text-secondary-custom hover:text-primary-custom"
                }`}
              >
                <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
              </button>
              {moreOpen && (
                <div
                  data-testid="nav-more-menu"
                  className="absolute right-0 mt-2 min-w-[180px] bg-primary-custom border border-subtle-custom shadow-2xl z-[60] animate-fade-in"
                >
                  {secondaryNav.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        data-testid={`nav-more-${item.id}`}
                        className={`block px-4 py-3 text-[11px] font-sans tracking-[0.18em] uppercase transition-colors duration-200 ${
                          isActive
                            ? "text-[var(--accent-bronze)]"
                            : "text-secondary-custom hover:text-primary-custom hover:bg-surface-custom"
                        }`}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Language Selector */}
            <LanguageSelector />

            {/* Theme Toggle Button — quieter, borderless */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-button"
              className="p-2 rounded-none transition-colors duration-300 text-secondary-custom hover:text-primary-custom"
              aria-label="Toggle Theme"
            >
              {theme === "light" ? (
                <Moon className="w-4 h-4" strokeWidth={1.5} />
              ) : (
                <Sun className="w-4 h-4" strokeWidth={1.5} />
              )}
            </button>

            {/* Thriver control — icon-only on desktop, quieter feel */}
            <Link
              to="/start-here"
              data-testid="nav-cta-button"
              aria-label={t("cta.thriver")}
              title={t("cta.thriver")}
              className="hidden sm:inline-flex items-center justify-center p-2 rounded-none transition-colors duration-300 text-secondary-custom hover:text-[var(--accent-bronze)]"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            </Link>

            {/* Mobile Menu Toggle — borderless */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-none text-secondary-custom hover:text-primary-custom transition-colors duration-300"
              aria-label="Toggle Menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" strokeWidth={1.5} />
              ) : (
                <Menu className="w-5 h-5" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Panel — compact, lighter typography */}
      {mobileMenuOpen && (
        <div 
          data-testid="mobile-menu-panel"
          className="lg:hidden fixed inset-x-0 top-20 bg-primary-custom border-b border-subtle-custom z-40 py-6 px-6 animate-fade-in transition-colors duration-500 max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          <nav className="flex flex-col divide-y divide-[var(--border-subtle)]/60">
            {navItems.filter((i) => i.id !== "home").map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.id}`}
                  className={`py-4 text-[13px] font-sans uppercase tracking-[0.18em] transition-colors ${
                    isActive
                      ? "text-[var(--accent-bronze)]"
                      : "text-primary-custom hover:text-[var(--accent-bronze)]"
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Main Page Area */}
      <main className="flex-grow">
        {location.pathname !== "/" && (
          <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 -mb-2 flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              data-testid="back-navigation"
              className="group inline-flex items-center gap-2.5 text-[10px] font-sans tracking-[0.3em] uppercase text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors duration-300"
            >
              <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.5} />
              <span>{t("nav.back")}</span>
            </button>
            <button
              onClick={() => navigate("/")}
              data-testid="close-navigation"
              aria-label={t("nav.close")}
              className="group inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.3em] uppercase text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors duration-300"
            >
              <span className="hidden sm:inline">{t("nav.close")}</span>
              <X className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90" strokeWidth={1.5} />
            </button>
          </div>
        )}
        {children}
      </main>

      {/* Premium Editorial Footer */}
      <footer className="bg-surface-custom border-t border-subtle-custom py-20 transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
          
          {/* Col 1: Institutional Statement */}
          <div className="md:col-span-2 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border border-subtle-custom flex items-center justify-center">
                <span className="font-serif text-xs font-semibold tracking-widest">T</span>
              </div>
              <span className="font-serif tracking-[0.2em] text-xs font-semibold uppercase">
                The Thriving Life 101
              </span>
            </div>
            <p className="font-serif text-sm italic text-secondary-custom max-w-md leading-relaxed">
              &quot;{t("footer.statement")}&quot;
            </p>
            <div className="text-[10px] font-sans tracking-[0.2em] text-secondary-custom uppercase">
              {t("footer.rights")}
            </div>
            <div className="flex items-center gap-4 pt-2">
              <ExternalLink
                href="https://www.instagram.com/thethrivinglife101/"
                aria-label="Follow The Thriving Life 101 on Instagram"
                data-testid="instagram-link"
                className="group inline-flex items-center gap-2.5 text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors duration-300"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-[10px] font-sans tracking-[0.2em] uppercase">@thethrivinglife101</span>
              </ExternalLink>
              <ExternalLink
                href="https://open.substack.com/pub/thethrivinglife101"
                aria-label="The Thriving Life 101 on Substack"
                data-testid="substack-link"
                className="group inline-flex items-center gap-2.5 text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors duration-300"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="11" x2="20" y2="11" />
                  <path d="M4 16h16v6l-8-4-8 4z" />
                </svg>
                <span className="text-[10px] font-sans tracking-[0.2em] uppercase">Substack</span>
              </ExternalLink>
            </div>
          </div>

          {/* Col 2: The Core Pillars */}
          <div className="space-y-4">
            <h4 className="text-[11px] font-sans tracking-[0.25em] uppercase text-primary-custom font-semibold">
              {t("footer.foundationsTitle")}
            </h4>
            <ul className="space-y-2 text-xs text-secondary-custom font-sans">
              {t("footer.foundations", { returnObjects: true }).map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>

          {/* Col 3: Institutional Links */}
          <div className="space-y-4">
            <h4 className="text-[11px] font-sans tracking-[0.25em] uppercase text-primary-custom font-semibold">
              {t("footer.institutionTitle")}
            </h4>
            <ul className="space-y-2 text-xs text-secondary-custom font-sans">
              <li>
                <Link to="/about" className="hover:text-primary-custom transition-colors">{t("footer.ourStory")}</Link>
              </li>
              <li>
                <Link to="/world" className="hover:text-primary-custom transition-colors">{t("footer.theWorld")}</Link>
              </li>
              <li>
                <Link to="/journal" className="hover:text-primary-custom transition-colors">{t("footer.theJournal")}</Link>
              </li>
              <li>
                <Link to="/community" data-testid="footer-community-link" className="hover:text-primary-custom transition-colors">{t("footer.community")}</Link>
              </li>
              <li>
                <Link to="/resources" className="hover:text-primary-custom transition-colors">{t("footer.resources")}</Link>
              </li>
            </ul>
          </div>

        </div>
        
        {/* Footnote Bar */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16 pt-8 border-t border-subtle-custom flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-[10px] text-secondary-custom uppercase tracking-[0.15em]">
            {t("footer.longTerm")}
          </div>
          <div className="text-[10px] text-secondary-custom uppercase tracking-[0.15em] flex items-center space-x-4">
            <button onClick={openPrivacy} data-testid="footer-privacy-prefs" className="hover:text-primary-custom transition-colors uppercase tracking-[0.15em]">{t("footer.privacyPrefs")}</button>
            <span>•</span>
            <Link to="/privacy-policy" data-testid="footer-privacy-policy" className="hover:text-primary-custom transition-colors uppercase tracking-[0.15em]">{t("footer.privacyPolicy")}</Link>
            <span>•</span>
            <button onClick={() => setTermsOpen(true)} data-testid="footer-institutional-terms" className="hover:text-primary-custom transition-colors uppercase tracking-[0.15em]">{t("footer.terms")}</button>
          </div>
        </div>
      </footer>
      <PolicyModal open={termsOpen} onClose={() => setTermsOpen(false)} tKey="terms" testId="terms" />
      <PolicyModal open={privacyPolicyOpen} onClose={() => setPrivacyPolicyOpen(false)} tKey="privacyPolicy" testId="privacy-policy" />
    </div>
  );
}
